import * as amplitude from "@amplitude/unified";

/**
 * Amplitude analytics wrapper (browser-only).
 *
 * - Initialises exactly once per app lifecycle (guarded by `initialised`).
 * - Session Replay is OFF by default. PEAK4 handles health data / PII, so
 *   replay only enables when VITE_AMPLITUDE_SESSION_REPLAY === "true" AND a
 *   replay sample rate is configured — flip this on only with privacy sign-off.
 * - All calls no-op safely when the API key is absent or before init.
 */

const API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY as string | undefined;
const SESSION_REPLAY_ENABLED =
  import.meta.env.VITE_AMPLITUDE_SESSION_REPLAY === "true";
const SESSION_REPLAY_SAMPLE_RATE = Number(
  import.meta.env.VITE_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE ?? "0"
);

let initialised = false;

export const AnalyticsEvent = {
  Login: "User Logged In",
  Logout: "User Logged Out",
  SignUp: "User Signed Up",
  ConsentDecision: "Consent Decision Made",
  DeviceConnected: "Wearable Connected",
  DeviceConnectStarted: "Wearable Connect Started",
  FaqOpened: "FAQ Opened",
  PasswordChanged: "Password Changed",
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

/** Initialise Amplitude once. Safe to call on every app mount. */
export const initAnalytics = (): void => {
  if (initialised || typeof window === "undefined" || !API_KEY) {
    return;
  }
  initialised = true;

  amplitude.initAll(API_KEY, {
    serverZone: "EU",
    analytics: {
      autocapture: true,
    },
    ...(SESSION_REPLAY_ENABLED && SESSION_REPLAY_SAMPLE_RATE > 0
      ? { sessionReplay: { sampleRate: SESSION_REPLAY_SAMPLE_RATE } }
      : {}),
  });
};

const ready = () => initialised && typeof window !== "undefined" && !!API_KEY;

/** Track a typed event with optional properties. */
export const trackEvent = (
  name: AnalyticsEventName,
  props?: Record<string, unknown>
): void => {
  if (!ready()) return;
  amplitude.track(name, props);
};

/**
 * Identify the logged-in user and start a fresh Amplitude session keyed to
 * the login. Sets the user id, a login-stamped session id, and core user
 * properties (no PII beyond what Amplitude already receives via autocapture).
 */
export const identifyUser = (user: {
  id: string;
  is_admin?: boolean;
  client_id?: string;
  subcontractor?: boolean;
}): void => {
  if (!ready()) return;

  amplitude.setUserId(user.id);
  // New session id pinned to this login event.
  amplitude.setSessionId(Date.now());

  const identify = new amplitude.Identify();
  if (user.is_admin !== undefined) identify.set("is_admin", user.is_admin);
  if (user.client_id) identify.set("client_id", user.client_id);
  if (user.subcontractor !== undefined) {
    identify.set("subcontractor", user.subcontractor);
  }
  amplitude.identify(identify);
};

/** Clear identity on logout so subsequent events are anonymous. */
export const resetAnalytics = (): void => {
  if (!ready()) return;
  amplitude.reset();
};
